import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTAButton from '../components/CTAButton';
import { loadCaptcha, submitWiderruf } from '../services/widerrufService';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  orderNumber: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  orderNumber?: string;
  captcha?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const VertragWiderrufen: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    orderNumber: '',
  });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState<string>('');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaLoading, setCaptchaLoading] = useState(true);

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // noindex this page
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      const existingTag = document.querySelector('meta[name="robots"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer('');
    try {
      const challenge = await loadCaptcha();
      setCaptchaSvg(challenge.svg);
      setCaptchaToken(challenge.token);
    } catch (error) {
      console.error('Error loading captcha:', error);
      setCaptchaSvg('');
      setCaptchaToken('');
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Bitte geben Sie Ihren Vornamen ein';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Bitte geben Sie Ihren Nachnamen ein';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse. Bitte überprüfen Sie Ihre Eingabe';
    }
    if (!formData.orderNumber.trim()) {
      newErrors.orderNumber = 'Bitte geben Sie Ihre Auftragsnummer ein';
    } else if (!/^#\d{6}$/.test(formData.orderNumber.trim())) {
      newErrors.orderNumber = 'Bitte im Format #211442 angeben (# und 6 Ziffern)';
    }
    if (!captchaAnswer.trim()) {
      newErrors.captcha = 'Bitte geben Sie den Sicherheitscode ein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Re-validate after first submit attempt
  useEffect(() => {
    if (touched) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, captchaAnswer, touched]);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Force the "#" + up to 6 digits schema while typing (e.g. #211442)
  const handleOrderNumberChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    updateField('orderNumber', digits ? `#${digits}` : '');
  };

  const getContainerClass = (hasError?: string) => {
    const baseClass =
      'flex items-center bg-[#f9fafb] border border-[#e2e8f0] rounded-md overflow-hidden';
    if (hasError && touched) {
      return `${baseClass} border-red-500 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500 focus-within:ring-opacity-20`;
    }
    return `${baseClass} focus-within:border-[#003a5d] focus-within:ring-2 focus-within:ring-[#003a5d] focus-within:ring-opacity-20`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // guard against double submit

    setTouched(true);

    if (!validateForm()) return;
    if (!captchaToken) {
      // Captcha failed to load – force a refresh and abort
      await refreshCaptcha();
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitWiderruf(formData, captchaToken, captchaAnswer.trim());
      if (result.success) {
        setStatus('success');
      } else {
        setStatus('error');
        // Captcha is single-use / may have expired – load a fresh one
        await refreshCaptcha();
      }
    } catch (error) {
      console.error('Error submitting Widerruf:', error);
      setStatus('error');
      await refreshCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h1 className="text-3xl text-center font-bold text-deep-blue mb-3">Vertrag widerrufen</h1>
          <p className="text-center text-gray-600 mb-8">
            Bitte füllen Sie die Angaben aus, um Ihren Vertrag zu widerrufen. Sie erhalten eine
            Eingangsbestätigung per E-Mail.
          </p>

          {status === 'success' ? (
            <div className="rounded-md border border-green-300 bg-green-50 p-6 text-center">
              <h2 className="text-xl font-semibold text-green-800 mb-2">
                Ihre Erklärung wurde übermittelt
              </h2>
              <p className="text-green-700">
                Vielen Dank. Wir haben Ihre Erklärung erhalten und senden Ihnen in Kürze eine
                Eingangsbestätigung per E-Mail.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Vorname */}
              <div>
                <div className={getContainerClass(errors.firstName)}>
                  <input
                    type="text"
                    placeholder="Vorname"
                    className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                  />
                </div>
                {errors.firstName && touched && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              {/* Nachname */}
              <div>
                <div className={getContainerClass(errors.lastName)}>
                  <input
                    type="text"
                    placeholder="Nachname"
                    className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                  />
                </div>
                {errors.lastName && touched && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>

              {/* E-Mail */}
              <div>
                <div className={getContainerClass(errors.email)}>
                  <input
                    type="email"
                    placeholder="E-Mail"
                    className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                  />
                </div>
                {errors.email && touched && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Auftragsnummer */}
              <div>
                <div className={getContainerClass(errors.orderNumber)}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={7}
                    placeholder="Auftragsnummer (startet mit #)"
                    className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                    value={formData.orderNumber}
                    onChange={(e) => handleOrderNumberChange(e.target.value)}
                  />
                </div>
                {errors.orderNumber && touched && (
                  <p className="mt-1 text-sm text-red-600">{errors.orderNumber}</p>
                )}
              </div>

              {/* Captcha */}
              <div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-700 whitespace-nowrap">Sicherheitscode:</span>
                  <div className="flex items-center min-h-[60px] min-w-[160px] bg-[#e9ecef] rounded-md overflow-hidden">
                    {captchaLoading ? (
                      <span className="px-4 text-sm text-gray-500">Lädt …</span>
                    ) : captchaSvg ? (
                      <div
                        className="select-none"
                        // SVG is generated server-side; rendered as the captcha image
                        dangerouslySetInnerHTML={{ __html: captchaSvg }}
                      />
                    ) : (
                      <span className="px-4 text-sm text-red-600">Fehler</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="text-sm text-blue-600 hover:underline whitespace-nowrap"
                  >
                    Neues Bild
                  </button>
                </div>
                <div className="mt-3">
                  <div className={getContainerClass(errors.captcha)}>
                    <input
                      type="text"
                      placeholder="Sicherheitscode eingeben"
                      autoComplete="off"
                      autoCapitalize="characters"
                      className="flex-grow py-3 px-3 bg-transparent border-0 focus:outline-none focus:ring-0"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                    />
                  </div>
                  {errors.captcha && touched && (
                    <p className="mt-1 text-sm text-red-600">{errors.captcha}</p>
                  )}
                </div>
              </div>

              {status === 'error' && (
                <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
                  Ihre Erklärung konnte leider nicht übermittelt werden. Bitte überprüfen Sie den
                  Sicherheitscode und versuchen Sie es erneut. Falls das Problem weiterhin besteht,
                  kontaktieren Sie uns bitte über das{' '}
                  <Link to="/kontaktformular" className="font-medium underline">
                    Kontaktformular
                  </Link>
                  .
                </div>
              )}

              <CTAButton
                type="submit"
                text={isSubmitting ? 'Wird gesendet …' : 'Widerruf erklären'}
                disabled={isSubmitting}
                className={`w-full ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VertragWiderrufen;
